import { useEffect, useState } from "react";
import { Observation, ObservationService } from "@/services/ObservationService";
import { AttachmentService, type AttachmentDetails } from "@/services/AttachmenteService";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { CodeService, type Code } from "@/services/code-service";
import { useForm } from "react-hook-form";
import { ValidationError } from "@/lib/http-client";
import { setFormErrors } from "@/lib/react-form-utils";
import FeatureFooter from "../feature/FeatureFooter";
import { Textarea } from "../ui/textarea";
import { formatDateDisplayString } from "@/lib/string-utils";
import GoogleMapPin from "../maps/GoogleMapPin";
import { SiteService } from "@/services/SiteService";
import type { GPSCoords } from "@/lib/image-utils";
import { useParams } from "react-router";
import { Loading } from "../Loading";
import { FileUploadCopied } from "../ui/FilesUploadCopied";
import ImageList from "../ImageList";

interface ObservationEditFormProps {
  observationId: string;
  onCancel: () => void;
  showFooter: boolean;
  attachmentColumn: boolean
}

const ObservationEditForm: React.FC<ObservationEditFormProps> = ({ observationId, onCancel, showFooter, attachmentColumn }) => {
  const params = useParams();
  const [attachmentDetails, setAttachmentDetails] = useState<AttachmentDetails[]>([]);
  const [observationCodes, setObservationCodes] = useState<Code[]>([]);
  const { register, handleSubmit, formState: { errors, isDirty }, setError, reset } = useForm<Observation>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [genericError, setGenericError] = useState<string[]>([]);
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [featureTypeId, setFeatureTypeId] = useState<string | null>(null);
  const [featureName, setFeatureName] = useState<string | null>(null);
  const [featureTypeName, setFeatureTypeName] = useState<string | null>(null);
  const [observedByName, setObservedByName] = useState<string | null>(null);
  const [observedAt, setObservedAt] = useState<string | null>(null);
  const [siteCoords, setSiteCoords] = useState<GPSCoords | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [observation, attachments, codes, site] = await Promise.all([
        ObservationService.getById(observationId),
        AttachmentService.getByEntityId(observationId),
        CodeService.getCodes2(),
        SiteService.getSiteById(params.siteId!),
      ]);

      if (!observation) {
        setGenericError(["Observation not found"]);
        return;
      }

      if (observation.featureId) {
        setFeatureId(observation.featureId);
      }

      if (observation.latitude && observation.longitude) {
        setSiteCoords({
          latitude: observation.latitude,
          longitude: observation.longitude,
        });
      } else if (site) {
        setSiteCoords({
          latitude: site.latitude ?? 0,
          longitude: site.longitude ?? 0,
        });
      }

      setAttachmentDetails(
        attachments
          .sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())
      );
      setObservedByName(observation.observedByName);
      setObservedAt(formatDateDisplayString(observation.observedAt));
      setFeatureTypeId(observation?.featureTypeId)
      setFeatureName(observation?.featureName);
      setFeatureTypeName(observation?.featureTypeName);
      setObservationCodes(codes.filter((c) => c.type === "ObservationType"));
      reset(observation);
      setIsLoading(false);
    };
    fetchData();
  }, [observationId, reset]);

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  const handleFormSubmit = async (data: Observation) => {
    try {
      data.latitude = siteCoords?.latitude ?? null;
      data.longitude = siteCoords?.longitude ?? null;
      await ObservationService.update(observationId, data);
      reset(data);
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormErrors<Observation>(error.errors, setError, data);

        console.log("Validation errors:", error.errors);
        const concatenated = Object.entries(error.errors)
          .filter(([key]) => key !== "name" && key !== "typeCodeId")
          .map(([key, value]) => `${key}: ${value}`)
          .join("");
        setGenericError(concatenated ? [concatenated] : []);
      }
    } finally {
      setIsSaveDisabled(true);
    }
  };

  return (
    <>
    <Loading message="Loading observation..." isLoading={isLoading} />
    <div className="flex flex-col h-full">
      <div className="text-red-600 text-sm">{genericError}</div> 
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <Label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name*
          </Label>
          <Input
            type="text"
            {...register("name", { required: "Name is required" })}
          />
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <Label htmlFor="description">Observations</Label>
          <Textarea
            placeholder="Enter observations..."
            id="description"
            {...register("description")}
          />
        </div>
        <div>
          <Label htmlFor="typeCodeId">Type</Label>
          <select
            id="typeCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1`}
            {...register("typeCodeId", { required: "Type is required" })}
          >
            {observationCodes.length > 0 && (
              <>
                {observationCodes.map((typeCode) => (
                  <option key={typeCode.id} value={typeCode.id}>
                    {typeCode.name}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
        <GoogleMapPin
          coords={siteCoords ?? { latitude: 51, longitude: -0.6 }} // Placeholder coords; replace with actual observation coords
          onChange={(coords) => {
            setSiteCoords(coords);
            setIsSaveDisabled(false);
          }}
         />
      <div className="flex gap-1 items-center">
        <Label>Observed By</Label>
        <div className="text-sm">{observedByName}</div>
      </div>     
      <div className="flex gap-1 items-center">
        <Label>Observed At</Label>
        <div className="text-sm">{observedAt}</div>
      </div>
      {/* <div className="flex gap-1 items-center">
        <Label>Feature</Label>
        <div className="text-sm">{featureName}</div>
      </div>
      <div className="flex gap-1 items-center">
        <Label>Feature Type</Label>
        <div className="text-sm">{featureTypeName}</div>
        {featureId && (
          <Link
            className="text-sm text-blue-500"
            to={featureTypeUrlBuilder(params.siteId!, featureId)}
          >
            {featureTypeName}
          </Link>
        )}
      </div> */}
      {showFooter && (<FeatureFooter featureId={featureId} featureTypeId={featureTypeId} featureName={featureName} featureTypeName={featureTypeName} />)}
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={() => {
              reset();
              onCancel();
            }}
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Save
          </Button>
        </div>
      </form>
      <div className="my-2">
        <FileUploadCopied
          entityId={observationId}
          onFileUploaded={(attachment) => {
            setAttachmentDetails((prev) => [
              {
                ...attachment,
              },
              ...prev,
            ]);
          }}
          buttonText="Select Images"
        />
      </div>
      <div className="flex-grow">
        <div className={`${attachmentColumn ? "flex flex-col md:flex-row gap-1" : "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4"}`}>
          <ImageList
            attachmentColumn={attachmentColumn}
            attachments={attachmentDetails}
            onDeleteAttachment={(attachmentId: string) => {
              try {
                AttachmentService.delete(attachmentId);
                setAttachmentDetails(prev => prev.filter(att => att.id !== attachmentId));
              } catch (error) {
                alert("Error deleting attachment");
                throw error;
              }
            }}
          />          
        </div>
      </div>
    </div>
    </>
  );
};

export default ObservationEditForm;
